import * as React from "react";
import { Tag } from "primereact/tag";

import styles from "./Pill.module.css";

/**
 * Тональные семейства статусов. Значения — в styles/tokens.css (--pill-*).
 *
 * Тон, а не статус: набор статусов у pda-front (~30) и pda-cfront (подмножество)
 * разный, поэтому карта «статус -> тон» живёт в коде портала, а не здесь.
 * См. docs/REDESIGN-PLAN.md §2.3 и §3.4.
 */
export type PillTone =
  | "blue"    /* в работе, разработка, на проверке */
  | "green"   /* согласовано, завершено, утверждено */
  | "amber"   /* приостановлено, решение не принято */
  | "red"     /* остановлено, отправлено на доработку */
  | "olive"   /* согласовано с учётом замечаний, выполнено КА */
  | "purple"  /* утверждено ГИП, исходные данные */
  | "rose"    /* отменена */
  | "gray";   /* новый, неактуально, не требует согласования */

export interface PillToneStyle {
  bg: string;
  color: string;
  dot: string;
}

/** Значения тона в виде var()-ссылок — чтобы не хардкодить цвета в порталах. */
export const toneStyle = (tone: PillTone): PillToneStyle => ({
  bg: `var(--pill-${tone}-bg)`,
  color: `var(--pill-${tone}-color)`,
  dot: `var(--pill-${tone}-dot)`,
});

export interface PillProps {
  label: React.ReactNode;
  /** Тон из токенов. Игнорируется, если заданы bg/color явно. */
  tone?: PillTone;
  /** Явный цвет фона. Приоритетнее tone. */
  bg?: string;
  /** Явный цвет текста. Приоритетнее tone. */
  color?: string;
  icon?: string;
  /** Явный цвет точки. Приоритетнее dot. */
  dotColor?: string;
  /**
   * Показать точку в цвете тона. В макетах точка есть только на карточных
   * экранах; в списках вместо неё иконка статуса — поэтому opt-in, не по умолчанию.
   */
  dot?: boolean;
  /**
   * tag  — статусная пилюля (по умолчанию);
   * chip — кликабельный фильтр с рамкой и фиксированной высотой.
   */
  variant?: "tag" | "chip";
  /** Только для chip: sm — 28px (десктоп), md — 34px (мобильная лента). */
  size?: "sm" | "md";
  /** Только для chip: включённый фильтр красится в свой тон, выключенный — белый. */
  active?: boolean;
  /** Число рядом с лейблом (счётчик в фильтре). 0 отображается, undefined — нет. */
  count?: number;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  title?: string;
  className?: string;
}

export const Pill: React.FC<PillProps> = ({
  label, tone, bg, color, icon, dotColor, dot,
  variant = "tag", size = "sm", active, count, disabled, onClick, title, className,
}) => {
  const t = tone ? toneStyle(tone) : undefined;
  const resolvedBg = bg ?? t?.bg;
  const resolvedColor = color ?? t?.color;
  const resolvedDot = dotColor ?? (dot ? t?.dot : undefined);

  const body = (
    <React.Fragment>
      {resolvedDot && !icon && <span className={styles[`pill-dot`]} style={{ backgroundColor: resolvedDot }} />}
      {icon && <i className={icon} aria-hidden="true" />}
      <span>{label}</span>
      {count !== undefined && <span className={styles[`chip-count`]}>{count}</span>}
    </React.Fragment>
  );

  if (variant === "chip") {
    /* Выключенный чип белый с волосяной рамкой; включённый берёт свой тон
       и красит рамку в цвет текста — так в макете «Замечаний». */
    const chipStyle: React.CSSProperties = active
      ? { background: resolvedBg, color: resolvedColor, borderColor: resolvedColor }
      : { background: "var(--bg-card)", color: "var(--text-tertiary)", borderColor: "var(--border-10)" };

    return (
      <button
        type="button"
        className={`${styles.chip} ${styles[size]}${className ? ` ${className}` : ``}`}
        style={chipStyle}
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-pressed={!!active}
      >
        {body}
      </button>
    );
  }

  return (
    <Tag
      rounded
      className={`${styles.pill}${className ? ` ${className}` : ``}`}
      style={{ background: resolvedBg, color: resolvedColor }}
      value={body} />
  );
};
